import {
  InteractionEvent,
  SignInIdentifier,
  type PasswordVerificationPayload,
} from '@logto/schemas';
import { conditional } from '@silverhand/essentials';
import { HTTPError, TimeoutError } from 'ky';
import { useCallback, useMemo, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import CaptchaContext from '@/Providers/CaptchaContextProvider/CaptchaContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { signInWithPasswordIdentifier } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import useCheckSingleSignOn from '@/hooks/use-check-single-sign-on';
import type { ErrorHandlers } from '@/hooks/use-error-handler';
import useErrorHandler from '@/hooks/use-error-handler';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import { resolveLoginIdentifier } from '@/utils/login-identifier';

import { useConfirmModal } from './use-confirm-modal';
import useGlobalRedirectTo from './use-global-redirect-to';
import useSubmitInteractionErrorHandler from './use-submit-interaction-error-handler';

const usePasswordSignIn = ({
  preserveInteraction = false,
}: { preserveInteraction?: boolean } = {}) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const { onSubmit: checkSingleSignOn } = useCheckSingleSignOn();
  const redirectTo = useGlobalRedirectTo();
  const { executeCaptcha } = useContext(CaptchaContext);

  const { t, i18n } = useTranslation();
  const { show } = useConfirmModal();
  const navigate = useNavigateWithPreservedSearchParams();
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();

  const { identifierInputValue, setForgotPasswordIdentifierInputValue } =
    useContext(UserInteractionContext);

  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  const handleError = useErrorHandler();
  const asyncSignIn = useApi(signInWithPasswordIdentifier);
  const preSignInErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.SignIn);

  const handleRedirectToForgotPassword = useCallback(() => {
    // A Handle never discovers or prefills its owner's private recovery contact.
    setForgotPasswordIdentifierInputValue(
      identifierInputValue?.type === SignInIdentifier.Username ? undefined : identifierInputValue
    );

    navigate({ pathname: '/forgot-password' }, { replace: true });
  }, [identifierInputValue, navigate, setForgotPasswordIdentifierInputValue]);

  const errorHandlers: ErrorHandlers = useMemo(
    () => ({
      'session.invalid_credentials': () => {
        setErrorMessage(
          i18n.resolvedLanguage?.toLowerCase().startsWith('zh')
            ? '账号或密码不正确，请检查后重试。'
            : 'The account or password is incorrect. Please check and try again.'
        );
      },
      global: () => {
        setErrorMessage(t('error.unknown'));
      },
      ...conditional(
        isForgotPasswordEnabled && {
          'password.expired': () => {
            show({
              type: 'alert',
              ModalContent: t('description.password_expired'),
              cancelText: 'description.password_expiration_reset',
              shouldCloseOnEsc: false,
              shouldCloseOnOverlayClick: false,
              onCancel: () => {
                handleRedirectToForgotPassword();
              },
            });
          },
        }
      ),
      ...preSignInErrorHandler,
    }),
    [
      handleRedirectToForgotPassword,
      i18n.resolvedLanguage,
      isForgotPasswordEnabled,
      preSignInErrorHandler,
      show,
      t,
    ]
  );

  const onSubmit = useCallback(
    async (payload: PasswordVerificationPayload) => {
      const { identifier } = payload;
      // The UI/context carries the public Handle. Encode only here, for every password caller.
      const username =
        identifier.type === SignInIdentifier.Username
          ? resolveLoginIdentifier(identifier.value, SignInIdentifier.Username)
          : undefined;
      if (username && !username.ok) {
        setErrorMessage(t('error.general_invalid', { types: [t('input.username')] }));
        return;
      }
      const nativePayload = username?.ok
        ? { ...payload, identifier: { type: SignInIdentifier.Username, value: username.provider } }
        : payload;
      const captchaToken = preserveInteraction ? undefined : await executeCaptcha();

      // Check if the email is registered with any SSO connectors. If the email is registered with any SSO connectors, we should not proceed to the next step
      if (identifier.type === SignInIdentifier.Email) {
        const result = await checkSingleSignOn(identifier.value);

        if (result) {
          return;
        }
      }

      const [error, result] = preserveInteraction
        ? await asyncSignIn(nativePayload, undefined, { preserveInteraction: true })
        : await asyncSignIn(nativePayload, captchaToken);

      if (error) {
        if (error instanceof HTTPError || error instanceof TimeoutError) {
          await handleError(error, errorHandlers);
        } else {
          // The generic upstream handler logs unknown objects, which can carry a request body.
          setErrorMessage(t('error.unknown'));
        }

        return;
      }

      if (result?.redirectTo) {
        await redirectTo(result.redirectTo);
      }
    },
    [
      asyncSignIn,
      checkSingleSignOn,
      errorHandlers,
      executeCaptcha,
      handleError,
      preserveInteraction,
      redirectTo,
      t,
    ]
  );

  return {
    errorMessage,
    clearErrorMessage,
    onSubmit,
  };
};

export default usePasswordSignIn;
